import { lazy, Suspense, useEffect, useRef } from 'react'
import { Route, Routes } from 'react-router-dom'
import { useAuth0 } from '@auth0/auth0-react'
import Layout from './components/Layout.jsx'
import ProtectedRoute from './components/ProtectedRoute.jsx'
import CookieConsentBanner from './components/CookieConsentBanner.jsx'
import GeoNotice from './components/GeoNotice.jsx'
import { identify, reset } from './services/mixpanel.js'
import { PropertyDataProvider } from './context/PropertyDataContext.jsx'
import { SavedPropertiesProvider } from './context/SavedPropertiesContext.jsx'
import { ListingsPreferencesProvider } from './context/ListingsPreferencesContext.jsx'
import { ComparisonProvider } from './context/ComparisonContext.jsx'
import { AffordabilityProvider } from './context/AffordabilityContext.jsx'
import { COMPETITORS } from './data/competitors.js'
import HomePage from './pages/HomePage.jsx'
import ListingsPage from './pages/ListingsPage.jsx'
import './App.css'

const PropertyDetailsPage = lazy(() => import('./pages/PropertyDetailsPage.jsx'))
const SellerHubPage = lazy(() => import('./pages/SellerHubPage.jsx'))
const BuyerHubPage = lazy(() => import('./pages/BuyerHubPage.jsx'))
const ProfilePage = lazy(() => import('./pages/ProfilePage.jsx'))
const ListingWizardPage = lazy(() => import('./pages/ListingWizardPage.jsx'))
const CalendarPage = lazy(() => import('./pages/CalendarPage.jsx'))
const AlsoListPage = lazy(() => import('./pages/AlsoListPage.jsx'))
const SellWithoutAgentPage = lazy(() => import('./pages/SellWithoutAgentPage.jsx'))
const SellInTownPage = lazy(() => import('./pages/SellInTownPage.jsx'))
const CompareHubPage = lazy(() => import('./pages/CompareHubPage.jsx'))
const CompareAlternativePage = lazy(() => import('./pages/CompareAlternativePage.jsx'))
const BlogPage = lazy(() => import('./pages/BlogPage.jsx'))
const BlogPostPage = lazy(() => import('./pages/BlogPostPage.jsx'))
const HouseValuationPage = lazy(() => import('./pages/HouseValuationPage.jsx'))
const HousePricesIndexPage = lazy(() => import('./pages/HousePricesIndexPage.jsx'))
const HousePricesPage = lazy(() => import('./pages/HousePricesPage.jsx'))
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage.jsx'))
const TermsPage = lazy(() => import('./pages/TermsPage.jsx'))
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicyPage.jsx'))
const ContactPage = lazy(() => import('./pages/ContactPage.jsx'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage.jsx'))

function AuthTracker() {
  const { user, isAuthenticated, isLoading } = useAuth0()
  const identified = useRef(false)

  useEffect(() => {
    if (isLoading) return
    if (isAuthenticated && user?.sub && !identified.current) {
      identified.current = true
      identify(user.sub, {
        $email: user.email,
        $name: user.name,
      })
    }
    if (!isAuthenticated && identified.current) {
      identified.current = false
      reset()
    }
  }, [isAuthenticated, isLoading, user])

  return null
}

function App({ authEnabled }) {
  return (
    <PropertyDataProvider>
      <SavedPropertiesProvider>
        <AffordabilityProvider>
        <ComparisonProvider>
        <ListingsPreferencesProvider>
          {authEnabled && <AuthTracker />}
          <CookieConsentBanner />
          <GeoNotice />
          <Layout authEnabled={authEnabled}>
        <Suspense fallback={<div className="page-loading" />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/listings" element={<ListingsPage />} />
          <Route path="/property/:propertyId" element={<PropertyDetailsPage />} />
          <Route
            path="/profile"
            element={
              authEnabled ? (
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              ) : (
                <ProfilePage />
              )
            }
          />
          <Route
            path="/sell"
            element={
              authEnabled ? (
                <ProtectedRoute>
                  <SellerHubPage />
                </ProtectedRoute>
              ) : (
                <SellerHubPage />
              )
            }
          />
          <Route
            path="/buy"
            element={
              authEnabled ? (
                <ProtectedRoute>
                  <BuyerHubPage />
                </ProtectedRoute>
              ) : (
                <BuyerHubPage />
              )
            }
          />
          <Route
            path="/sell/new"
            element={
              authEnabled ? (
                <ProtectedRoute>
                  <ListingWizardPage />
                </ProtectedRoute>
              ) : (
                <ListingWizardPage />
              )
            }
          />
          <Route
            path="/calendar"
            element={
              authEnabled ? (
                <ProtectedRoute>
                  <CalendarPage />
                </ProtectedRoute>
              ) : (
                <CalendarPage />
              )
            }
          />
          <Route path="/also-list" element={<AlsoListPage />} />
          <Route path="/sell-without-an-agent" element={<SellWithoutAgentPage />} />
          <Route path="/sell-without-an-agent/:slug" element={<SellInTownPage />} />
          <Route path="/house-valuation" element={<HouseValuationPage />} />
          <Route path="/house-prices" element={<HousePricesIndexPage />} />
          <Route path="/house-prices/:slug" element={<HousePricesPage />} />
          <Route path="/compare-estate-agents" element={<CompareHubPage />} />
          {/* react-router can't express a ":slug" segment with a static
              "-alternative" suffix, so the routes are generated from the
              competitor list — same source of truth the prerenderer and sitemap
              read. See src/data/competitors.js. */}
          {COMPETITORS.map((competitor) => (
            <Route
              key={competitor.slug}
              path={`/${competitor.slug}-alternative`}
              element={<CompareAlternativePage slug={competitor.slug} />}
            />
          ))}
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/cookies" element={<CookiePolicyPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
        </Layout>
        </ListingsPreferencesProvider>
        </ComparisonProvider>
        </AffordabilityProvider>
      </SavedPropertiesProvider>
    </PropertyDataProvider>
  )
}

export default App
